import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import matterService from '@api/v1/matterService'
import timeEntryService from '@api/v1/timeEntryService'
import LoadingSpinner from '@components/common/LoadingSpinner'
import DocumentCard from '@components/common/DocumentCard'
import MatterForm from '@components/matters/MatterForm'
import ConfirmModal from '@components/common/ConfirmModal'
import { toast } from '@store/toastStore'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const TABS = [
  'Overview', 'Contacts', 'Time Entries',
  'Documents', 'Bills', 'Notes', 'Activities',
]

const LinkedItemsTab = ({ data, isLoading: loading, emptyMessage, columns }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100
        p-12 flex justify-center"
      >
        <LoadingSpinner message="Loading..." />
      </div>
    )
  }
  const items = data?.items || []
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100
        p-12 text-center"
      >
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    )
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100
      overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
        <p className="text-xs text-gray-500">
          {data?.total ?? items.length} records
        </p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`${col.align === 'right' ? 'text-right' : 'text-left'}
                  px-5 py-3 text-xs font-semibold text-gray-500
                  uppercase tracking-wider`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {items.map((item, i) => (
            <tr
              key={item.id || i}
              className="hover:bg-gray-50/50 transition-colors"
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-5 py-3.5
                    ${col.align === 'right' ? 'text-right' : ''}
                    ${col.maxWidth ? 'max-w-xs' : ''}`}
                >
                  <p className={`text-sm
                    ${col.bold ? 'font-medium text-gray-900' : 'text-gray-600'}
                    ${col.maxWidth ? 'truncate' : ''}`}
                  >
                    {col.render ? col.render(item) : (item[col.key] || '--')}
                  </p>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const DetailRow = ({ label, value }) => (
  <div className="flex items-start py-2.5 border-b border-gray-50 last:border-0">
    <dt className="w-40 shrink-0 text-sm text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900">{value || '--'}</dd>
  </div>
)

const MatterDetail = () => {
  useDocumentTitle('Matter Detail')
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('Overview')
  const [editing, setEditing] = useState(false)
  const [showArchive, setShowArchive] = useState(false)

  const matterQuery = useQuery({
    queryKey: ['matter', id],
    queryFn: () => matterService.getMatter(id),
    enabled: !!id,
  })
  // Spec 148: the matter read through `useReadState`, not `isLoading`/`error`.
  // Both are false for a PAUSED read, which therefore fell through to "Matter not
  // found". And cb-api's 404 (a matter that does not exist) read as "Failed to
  // load matter".
  const matterState = useReadState('matter-detail', matterQuery)
  const { data: matter, error } = matterQuery

  const updateMutation = useMutation({
    mutationFn: (data) => matterService.updateMatter(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matter', id] })
      queryClient.invalidateQueries({ queryKey: ['matters'] })
      setEditing(false)
      toast.success('Matter updated')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to update matter')
    },
  })

  const archiveMutation = useMutation({
    mutationFn: () => matterService.archiveMatter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matters'] })
      toast.success('Matter archived')
      navigate('/matters')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to archive matter')
    },
  })

  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['matterContacts', id],
    queryFn: () => matterService.getMatterContacts(id),
    enabled: activeTab === 'Contacts' && !!id,
  })

  const { data: timeData, isLoading: timeLoading } = useQuery({
    queryKey: ['matterTimeEntries', id],
    queryFn: () => timeEntryService.getBillingEntries({
      matterId: id, limit: 50,
    }),
    enabled: activeTab === 'Time Entries' && !!id,
  })

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ['matterActivities', id],
    queryFn: async () => {
      const data = await matterService.getMatterActivities(id)
      return { items: data, total: data.length }
    },
    enabled: activeTab === 'Activities' && !!id,
  })

  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['matterDocuments', id],
    queryFn: async () => {
      const data = await matterService.getMatterDocuments(id)
      return { items: data, total: data.length }
    },
    enabled: activeTab === 'Documents' && !!id,
  })

  const { data: billsData, isLoading: billsLoading } = useQuery({
    queryKey: ['matterBills', id],
    queryFn: async () => {
      const data = await matterService.getMatterBills(id)
      return { items: data, total: data.length }
    },
    enabled: activeTab === 'Bills' && !!id,
  })

  const matterNumber = matter?.number
  const { data: notesData, isLoading: notesLoading } = useQuery({
    queryKey: ['matterNotes', id, matterNumber],
    queryFn: async () => {
      const data = await matterService.getMatterNotes(id, matterNumber)
      return { items: data, total: data.length }
    },
    enabled: activeTab === 'Notes' && !!id && !!matterNumber,
  })

  // `idle` shares the spinner: it needs `enabled: false`, i.e. no `:id`, which this
  // route cannot match. An unhandled state would otherwise render "Matter not found".
  if (matterState === 'loading' || matterState === 'idle') {
    return (
      <div className="max-w-7xl mx-auto p-12 flex justify-center">
        <LoadingSpinner message="Loading matter..." />
      </div>
    )
  }

  const backToMatters = (
    <button
      onClick={() => navigate('/matters')}
      className="text-sm text-cb-accent-text hover:underline"
    >
      Back to Matters
    </button>
  )
  const tryAgain = (
    <button
      type="button"
      onClick={() => matterQuery.refetch()}
      className="mr-4 rounded-lg border border-rc-control bg-white px-3 py-1.5 text-xs font-medium text-cb-accent-text hover:bg-gray-50"
    >
      Try again
    </button>
  )

  if (matterState === 'blocked') {
    return (
      <div role="alert" className="max-w-7xl mx-auto p-12 text-center">
        <p className="text-sm font-medium text-amber-900 mb-2">Can’t reach the server, so this matter could not be read.</p>
        <p className="text-sm text-gray-600 mb-4">
          This is not a matter that doesn’t exist — no request was sent for it at all.
        </p>
        {tryAgain}
        {backToMatters}
      </div>
    )
  }

  // A 404 is cb-api saying the matter does not exist, so it falls through to "not
  // found". Every other failure says nothing about whether it exists.
  if (error && error.response?.status !== 404) {
    return (
      <div role="alert" className="max-w-7xl mx-auto p-12 text-center">
        <p className="text-sm text-red-700 mb-2">
          Failed to load matter: {error.response?.data?.error || error.message}
        </p>
        <p className="text-sm text-gray-600 mb-4">
          This is not a matter that doesn’t exist — nothing was read.
        </p>
        {tryAgain}
        {backToMatters}
      </div>
    )
  }

  if (!matter) {
    return (
      <div className="max-w-7xl mx-auto p-12 text-center">
        <p className="text-sm text-gray-500 mb-4">Matter not found</p>
        {backToMatters}
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/matters')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex
            items-center gap-1"
        >
          <svg
            className="w-4 h-4" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" strokeWidth={2}
          >
            <path
              strokeLinecap="round" strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back to Matters
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display text-gray-900">
              {matter.name}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {matter.number && (
                <span className="mr-4">{matter.number}</span>
              )}
              {matter.clientName && (
                <span className="mr-4">{matter.clientName}</span>
              )}
              <span className={`text-xs font-medium px-2.5 py-0.5
                rounded-full ${
                  matter.status === 'Active'
                    ? 'text-cb-teal-dark bg-cb-teal/10'
                    : 'text-gray-500 bg-gray-100'
                }`}
              >
                {matter.status}
              </span>
            </p>
          </div>
          {activeTab === 'Overview' && !editing && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowArchive(true)}
                className="px-4 py-2 text-sm font-medium text-red-600
                  bg-red-50 rounded-lg hover:bg-red-100
                  transition-colors"
              >
                Archive
              </button>
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 text-sm font-medium text-white
                  bg-cb-accent rounded-lg hover:bg-cb-accent/90
                  transition-colors shadow-sm"
              >
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Archive Confirm */}
      {showArchive && (
        <ConfirmModal
          title="Archive Matter"
          message={`Are you sure you want to archive "${matter.name}"? It can be restored later.`}
          confirmLabel="Archive"
          variant="danger"
          isLoading={archiveMutation.isPending}
          onConfirm={() => archiveMutation.mutate()}
          onCancel={() => setShowArchive(false)}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setEditing(false) }}
              className={`pb-3 text-sm font-medium border-b-2
                transition-colors ${
                  activeTab === tab
                    ? 'text-cb-accent-text border-cb-accent'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'Overview' && editing && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100
          p-6"
        >
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Edit Matter
          </h3>
          {updateMutation.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200
              rounded-lg text-sm text-red-600"
            >
              {updateMutation.error.message || 'Failed to update matter'}
            </div>
          )}
          <MatterForm
            matter={matter}
            onSave={(formData) => updateMutation.mutate(formData)}
            onCancel={() => setEditing(false)}
            saving={updateMutation.isPending}
          />
        </div>
      )}

      {activeTab === 'Overview' && !editing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border
            border-gray-100 p-5"
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Matter Details
            </h3>
            <dl>
              <DetailRow label="Matter Name" value={matter.name} />
              <DetailRow label="Matter Number" value={matter.number} />
              {matter.clientName && (
                <DetailRow label="Client" value={matter.clientName} />
              )}
              {matter.clientNumber && (
                <DetailRow label="Client Number" value={matter.clientNumber} />
              )}
              <DetailRow label="Status" value={matter.status} />
              {matter.practiceArea && (
                <DetailRow
                  label="Practice Area" value={matter.practiceArea}
                />
              )}
              {matter.feeArrangement && (
                <DetailRow
                  label="Fee Arrangement" value={matter.feeArrangement}
                />
              )}
              {matter.source && (
                <DetailRow label="Source" value={matter.source} />
              )}
            </dl>
          </div>
          <div className="bg-white rounded-xl shadow-sm border
            border-gray-100 p-5"
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              People & Dates
            </h3>
            <dl>
              {matter.responsibleAttorney && (
                <DetailRow
                  label="Responsible Atty"
                  value={matter.responsibleAttorney}
                />
              )}
              {matter.billingPartner && (
                <DetailRow
                  label="Billing Partner" value={matter.billingPartner}
                />
              )}
              {matter.referredBy && (
                <DetailRow label="Referred By" value={matter.referredBy} />
              )}
              {matter.openDate && (
                <DetailRow
                  label="Open Date"
                  value={dayjs(matter.openDate).format('MMM D, YYYY')}
                />
              )}
              {matter.closeDate && (
                <DetailRow
                  label="Close Date"
                  value={dayjs(matter.closeDate).format('MMM D, YYYY')}
                />
              )}
              {!matter.responsibleAttorney && !matter.billingPartner
                && !matter.openDate && !matter.closeDate && (
                <p className="text-sm text-gray-500 py-2">
                  No additional details available
                </p>
              )}
            </dl>
          </div>
        </div>
      )}

      {activeTab === 'Contacts' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100
          overflow-hidden"
        >
          {contactsLoading ? (
            <div className="p-12 flex justify-center">
              <LoadingSpinner message="Loading contacts..." />
            </div>
          ) : !contacts || contacts.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-gray-500">
                No contacts linked to this matter
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Phone</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {contacts.map((contact, i) => (
                  <tr
                    key={contact.id || i}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-medium text-gray-900">
                        {contact.fullName}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-600">
                        {contact.contactType || '--'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-600">
                        {contact.company || '--'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-600">
                        {contact.email || '--'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-600">
                        {contact.phone || '--'}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'Time Entries' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100
          overflow-hidden"
        >
          {timeLoading ? (
            <div className="p-12 flex justify-center">
              <LoadingSpinner message="Loading time entries..." />
            </div>
          ) : !timeData?.entries || timeData.entries.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-gray-500">
                No time entries for this matter
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Timekeeper</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Hours</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold
                    text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {timeData.entries.map((entry, i) => (
                  <tr
                    key={entry.id || i}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-600">
                        {entry.date
                          ? dayjs(entry.date).format('MMM D')
                          : '--'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 max-w-xs">
                      <p className="text-sm text-gray-600 truncate">
                        {entry.description || entry.name || '--'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-600">
                        {entry.timekeeper || '--'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {entry.hours.toFixed(1)}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <p className="text-sm text-gray-600">
                        {entry.billableValue > 0
                          ? `$${entry.billableValue.toFixed(2)}`
                          : '--'}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 bg-gray-50/50">
                  <td colSpan={3} className="px-5 py-3">
                    <p className="text-sm font-semibold text-gray-700">
                      Total
                    </p>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {timeData.entries
                        .reduce((sum, e) => sum + e.hours, 0)
                        .toFixed(1)}
                    </p>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      ${timeData.entries
                        .reduce((sum, e) => sum + e.billableValue, 0)
                        .toFixed(2)}
                    </p>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {activeTab === 'Documents' && (
        documentsLoading ? (
          <div className="bg-white rounded-xl shadow-sm border
            border-gray-100 p-12 flex justify-center"
          >
            <LoadingSpinner message="Loading documents..." />
          </div>
        ) : !documentsData?.items || documentsData.items.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border
            border-gray-100 p-12 text-center"
          >
            <p className="text-sm text-gray-500">
              No documents linked to this matter
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {documentsData.items.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} />
            ))}
          </div>
        )
      )}

      {activeTab === 'Bills' && (
        <LinkedItemsTab
          data={billsData}
          isLoading={billsLoading}
          emptyMessage="No bills linked to this matter"
          columns={[
            { key: 'name', header: 'Bill', bold: true },
            { key: 'postingStatus', header: 'Status' },
            { key: 'responsibleAttorney', header: 'Responsible Atty' },
            {
              key: 'createdDate', header: 'Date',
              render: (r) =>
                r.createdDate
                  ? dayjs(r.createdDate).format('MMM D, YYYY')
                  : '--',
            },
          ]}
        />
      )}

      {activeTab === 'Notes' && (
        <LinkedItemsTab
          data={notesData}
          isLoading={notesLoading}
          emptyMessage="No notes linked to this matter"
          columns={[
            { key: 'name', header: 'Note', bold: true },
            { key: 'description', header: 'Description', maxWidth: true },
            {
              key: 'createdDate', header: 'Created',
              render: (r) =>
                r.createdDate
                  ? dayjs(r.createdDate).format('MMM D, YYYY')
                  : '--',
            },
            { key: 'creator', header: 'Creator' },
          ]}
        />
      )}

      {activeTab === 'Activities' && (
        <LinkedItemsTab
          data={activitiesData}
          isLoading={activitiesLoading}
          emptyMessage="No activities linked to this matter"
          columns={[
            { key: 'subject', header: 'Subject', bold: true },
            { key: 'notes', header: 'Notes', maxWidth: true },
            { key: 'contact', header: 'Contact' },
            { key: 'scheduledFor', header: 'Scheduled For' },
            {
              key: 'createdDate', header: 'Created',
              render: (r) =>
                r.createdDate
                  ? dayjs(r.createdDate).format('MMM D, YYYY')
                  : '--',
            },
          ]}
        />
      )}
    </div>
  )
}

export default MatterDetail
